/**
 * 商务代言系统（2026-10-30 批，设计 canonical = docs/玩法设计.md §六「商务代言」；数值旋钮全部在 GameConfig ENDORSE_*）。
 *
 * 职责切分（仿 DirectSignSystem 头注释惯例）：本系统只管「派发决策+合约账本」——何时掷（rollMonthly，GameLoop 月结尾
 * 与直签同窗）、掷中挑哪个品牌（递约带软分布+资格过滤）、报价怎么算（三因子通式）、签约/放弃/月结/解约的账。
 * **呈现不在这里**：送达链（经纪人 VN→签约弹窗·重档）、官宣微博弹窗、解约/晋升的经纪人通报，全部经
 * pendingBrandOffer / pendingAnnounces 落档 + noticeQueue 待通报队列交给 UI 工位消费。
 * 官宣口径（2026-10-30 茄子令）：签约当刻只入队（pendingAnnounces），**玩家点「确认行程」、月度执行之前**
 * 在决策页弹 media 微博弹窗（博主名=品牌名）——挂在确认行程链 `app.playAnnounceThen`，与特殊剧情 media
 * 同一只 MediaPanel；⛔ 不在送达链/入场档里弹、⛔ 不再往微博面板信息流发帖。
 *
 * 红线（RankSystem 晋升三门口径「口碑只能靠演好戏拿」，桩测有断言）：
 *  - 商务只给钱+名气/粉丝，任何分支都给不出正口碑；
 *  - 微商签约掉口碑是负向，走 addAttribute 总闸（黑料缠身 ×1.5 自动生效）——本系统 ⛔ 直接 setAttribute。
 *
 * 口径防呆：ENDORSER_PAY / PAY_BY_ROLE_LEVEL 都是 ×1 相对口径，报价统一 ×GameConfig.PAY_SCALE
 * （表管结构、SCALE 管量级）——⛔ 任何基准表预乘 SCALE（双重乘法）。
 */
import { GameConfig } from '../core/GameConfig';
import { GameData } from '../data/GameData';
import { BrandDef, BrandTier, EndorseTitle, EndorsementContract, FixedMedia, PendingBrandAnnounce, PendingBrandOffer } from '../data/types';
import { GameState } from './GameState';

/** 品牌档中文名（台词/UI 共用） */
export const BRAND_TIER_NAMES: Record<BrandTier, string> = { ws: '微商', cl: '潮流', xf: '新消费', gm: '国民', gs: '高奢' };
/** 品类中文名（台词/UI 共用） */
export const BRAND_CATEGORY_NAMES: Record<string, string> = {
    mei: '美妆护肤', chuan: '服饰', shi: '食品饮料', shu: '3C数码', che: '汽车', you: '游戏互联网', bao: '珠宝高奢', jia: '母婴家居',
};
/** title 中文名 */
export const ENDORSE_TITLE_NAMES: Record<EndorseTitle, string> = { friend: '品牌挚友', ambassador: '品牌大使', spokesman: '品牌代言人' };

/**
 * 经纪人 VN 段台词——**canonical 文案源 = docs/品牌文案填写表.md §三**（茄子工作流：改文案先改表、导码同步此处）。
 * 2026-10-30 瘦身定稿：只写「点名品牌+务实点评+决定权」，⛔ 台词出现报酬/年限/title——那些由签约弹窗显示
 * （台词念报价=念 PPT），插值只留 {品牌名}（fillTemplate）。档位姿态的唯一载体：微商=钱虚掉形象提醒、
 * 潮流=轻快、新消费=务实、国民=稳重、高奢=掂量履历。
 * 高奢按递约 title 分组（2026-10-30 茄子纠错）：挚友=Lv.4~6 押潜力股的单次活动约、大使=Lv.7+ 考察期长约；
 * 代言人无递约条（晋升产生，走 noticeQueue 通报）。
 */
const BROKER_LINES: Record<Exclude<BrandTier, 'gs'>, string[]> = {
    ws: [
        '{品牌名}递了代言约。报酬到位，就是产品水平参差，签了容易被嘲，你自己权衡。',
        '我这边收到了{品牌名}递的代言邀约，报酬给得很实在，就是这家风评一般。',
    ],
    cl: [
        '{品牌名}递了代言约。钱不多，但调性是真合——他们家挑人看对味，不看咖位。',
        '收到了{品牌名}的递约，这家风评还行，但预算不多。',
    ],
    xf: [
        '{品牌名}递了代言约。他们看你的带货数据，不是看热搜，算是实在的生意。',
        '{品牌名}想签你，产品线正往上走，签下来对你没坏处。',
    ],
    gm: [
        '{品牌名}递了代言约。国民牌子露脸面广，代价是全年配合宣传，档期要留出来。',
        '{品牌名}想签你。他们要求口碑干净，你最近有没有负面，自己心里有数。',
    ],
};

/** 高奢按递约 title 分组的台词（挚友=单次活动约语义；大使=考察期长约语义；代言人无递约条） */
const GS_TITLE_LINES: Record<EndorseTitle, string[]> = {
    friend: [
        '{品牌名}看上你了，给的是品牌挚友——单次合作，钱不多，但这个圈子的大门开了。',
        '{品牌名}递来一个品牌挚友的位置，就一次活动，报酬是象征性的——可这种牌子肯点头，履历上就是一笔。',
    ],
    ambassador: [
        '{品牌名}递了品牌大使的约。钱不是重点，这牌子在履历上的分量才是——他们考察期长，别出岔子。',
        '{品牌名}只肯先给大使的位置，做得好才有下一步。',
    ],
    spokesman: [
        '{品牌名}这次直接递的是品牌代言人的约，没有考察期——这种牌子肯跳过流程，说明他们是真的想要你。',
        '{品牌名}把代言人的位置直接递过来了。顶奢很少这么签，他们算过账。',
    ],
};

/** 待通报队列条目（月结产出的被动事件——品牌解约/约满/考察期晋升；UI 决策页消费后弹经纪人 VN） */
export interface EndorseNotice {
    kind: 'terminated' | 'expired' | 'promoted' | 'breached';
    /** 经纪人口吻的完整通报句（已含金额插值） */
    text: string;
    /** 品牌解约时的违约金额（终值；expired/promoted 无） */
    penalty?: number;
    brandId: string;
}

export interface EndorseResult {
    ok: boolean;
    /** 失败原因/成功回执（经纪人口吻，showHint 或收尾 VN 用） */
    detail?: string;
    /** 微商签约专属：随机网友群嘲句（填写表 §四；2026-10-30 茄子令删掉签约后的二次气泡，字段保留给文案表/桩测） */
    mockText?: string;
}

export class EndorsementSystem {
    private static instance: EndorsementSystem;
    static getInstance(): EndorsementSystem {
        if (!EndorsementSystem.instance) EndorsementSystem.instance = new EndorsementSystem();
        return EndorsementSystem.instance;
    }
    private constructor() { }

    /** 月结产出的待通报队列（品牌解约/约满/晋升）；UI 消费一条 shift 一条 */
    readonly noticeQueue: EndorseNotice[] = [];

    private absMonth(): number {
        const p = GameState.getInstance().player;
        return p.year * 12 + p.month;
    }

    private rank(): number {
        return GameState.getInstance().player.rank;
    }

    // ===== 报价（三因子通式） =====

    /** 品牌基准年费（×1 口径；挚友=代言基准×10%） */
    private baseFee(tier: BrandTier, title: EndorseTitle): number {
        const rank = Math.max(1, Math.min(this.rank(), GameConfig.ENDORSE_PAY_BY_RANK.length));
        const spokesmanBase = GameConfig.ENDORSE_PAY_BY_RANK[rank - 1] || 0;
        if (title === 'ambassador') return Math.round(spokesmanBase * 0.5);
        if (title === 'friend') return Math.round(spokesmanBase * 0.1);
        return spokesmanBase;
    }

    /** 名气系数 = clamp(名气÷本咖位名气门槛, 0.5~2.0)；门槛≤0 防护=1 */
    private fameMult(): number {
        const state = GameState.getInstance();
        const rank = this.rank();
        const threshold = (GameConfig.RANK_FAME_THRESHOLDS[rank - 1] ?? 0) as number;
        if (threshold <= 0) return 1;
        const raw = state.getFame() / threshold;
        return Math.max(GameConfig.ENDORSE_FAME_MULT_MIN, Math.min(GameConfig.ENDORSE_FAME_MULT_MAX, raw));
    }

    /** 终值报价：基准 × 品牌档预算系数 × 名气系数 × PAY_SCALE（挚友=单次、长约=年费） */
    quoteFee(brand: BrandDef, title: EndorseTitle): number {
        const budget = GameConfig.ENDORSE_TIER_BUDGET[brand.tier] ?? 1;
        return Math.floor(this.baseFee(brand.tier, title) * budget * this.fameMult() * GameConfig.PAY_SCALE);
    }

    // ===== 资格与档位 =====

    /** 口碑档资格：T1（≤-301）仅微商；T2（≤-101）或黑料缠身 → 国民/高奢不递（黑红联动） */
    private tierAllowed(tier: BrandTier): boolean {
        const state = GameState.getInstance();
        const rep = state.getAttribute('reputation');
        if (rep <= GameConfig.REP_BAND_EDGES[0]) return tier === 'ws';
        if (rep <= GameConfig.REP_BAND_EDGES[1] || state.hasTag('TAG_401')) return tier === 'ws' || tier === 'cl' || tier === 'xf';
        return true;
    }

    /** 黑红联动判定（T2 语义：口碑较低档或黑料缠身） */
    private inT2Zone(): boolean {
        const state = GameState.getInstance();
        return state.getAttribute('reputation') <= GameConfig.REP_BAND_EDGES[1] || state.hasTag('TAG_401');
    }

    /** 爆款状态：最近完结作品挂爆标（boom 快照，§3.4f 同源），完结月起 N 月内（读履历快照，⛔ 回表） */
    private inBoomWindow(abs: number): boolean {
        const film = GameState.getInstance().player.filmography ?? [];
        const last = film[film.length - 1];
        if (!last || !last.boom) return false;
        return abs - last.month <= GameConfig.ENDORSE_BOOM_MONTHS;
    }

    // ===== 月掷递约（GameLoop 月结尾调用，与直签同窗） =====

    /**
     * 月掷：至多挂 1 个待签（强制二选一模型下不存在并存）；掷中按递约带软分布挑档、
     * 从该档品牌池挑品牌（过滤已放弃/在约品类），挑不到=本月无约（池薄属设计内）。
     */
    rollMonthly(): boolean {
        const state = GameState.getInstance();
        const p = state.player;
        if (p.pendingBrandOffer) return false;
        const abs = this.absMonth();
        const rank = this.rank();
        const idx = Math.max(0, Math.min(rank - 1, GameConfig.ENDORSE_ROLL_RATE_BY_RANK.length - 1));
        let rate = GameConfig.ENDORSE_ROLL_RATE_BY_RANK[idx] as number;
        if (rate <= 0) return false;
        if (this.inBoomWindow(abs)) rate *= GameConfig.ENDORSE_BOOM_MULT;
        if (this.inT2Zone()) rate *= GameConfig.ENDORSE_T2_RATE_MULT;
        if (Math.random() >= rate) return false;

        // 掷中后加权抽档（挑空剔除重抽，⛔ 同一次掷点直接作废——2026-10-30 茄子实测「高咖位代言变稀」主因之一）
        const picked = this.pickTierWithBrand(rank, abs);
        if (!picked) return false;
        const { tier, brand } = picked;

        const title = this.titleForTier(tier);
        const years = title === 'friend' ? 0 : (tier === 'gs' && title === 'ambassador' ? 1 : (Math.random() < 0.5 ? 1 : 2));
        // 挚友：签约当月起，玩家在行程工作栏自行排一周完成（2026-11-02 茄子令：不出品牌定档周）
        const offer: PendingBrandOffer = {
            brandId: brand.id,
            title,
            fee: this.quoteFee(brand, title),
            years,
            month: abs,
            brokerLine: this.fillTemplate(this.pickLine(brand.tier, title), brand),
        };
        p.pendingBrandOffer = offer;
        return true;
    }

    /**
     * 递约带软分布（2026-10-30 改「加权抽档、抽空剔除重抽」）：按咖位行权重归一化**加权随机**挑档
     * （保留低权重惊喜——高奢 Lv.4~6 押潜力股是 2% 小概率事件，排序制会让它永远轮不到）；
     * 抽中的档挑不到品牌（池薄/品类锁/已放弃）→ **剔除该档按剩余权重重抽**，同一次掷点不浪费。
     * 爆款期档位上浮一级（权重表取下一行）；T1/T2 资格外档清零。
     */
    private pickTierWeighted(rank: number, abs: number, exclude: Set<BrandTier> = new Set()): BrandTier | null {
        const rowIdx0 = Math.max(0, Math.min(rank - 1, GameConfig.ENDORSE_TIER_WEIGHTS.length - 1));
        const rowIdx = this.inBoomWindow(abs) ? Math.min(rowIdx0 + 1, GameConfig.ENDORSE_TIER_WEIGHTS.length - 1) : rowIdx0;
        const weights = (GameConfig.ENDORSE_TIER_WEIGHTS[rowIdx] ?? []) as number[];
        const tiers: BrandTier[] = ['ws', 'cl', 'xf', 'gm', 'gs'];
        let total = 0;
        const allowed: { tier: BrandTier; w: number }[] = [];
        for (let i = 0; i < tiers.length; i++) {
            const w = weights[i] ?? 0;
            if (w <= 0 || !this.tierAllowed(tiers[i]) || exclude.has(tiers[i])) continue;
            allowed.push({ tier: tiers[i], w });
            total += w;
        }
        if (!allowed.length || total <= 0) return null;
        let roll = Math.random() * total;
        for (const a of allowed) {
            roll -= a.w;
            if (roll < 0) return a.tier;
        }
        return allowed[allowed.length - 1].tier;
    }

    /** 掷中后逐档挑品牌：加权抽档→挑空剔除重抽，全空=本月无约 */
    private pickTierWithBrand(rank: number, abs: number): { tier: BrandTier; brand: BrandDef } | null {
        const tried = new Set<BrandTier>();
        for (;;) {
            const tier = this.pickTierWeighted(rank, abs, tried);
            if (!tier) return null;
            const brand = this.pickBrand(tier, abs);
            if (brand) return { tier, brand };
            tried.add(tier);   // 该档挑空：剔除权重池，按剩余权重重抽
        }
    }

    private pickBrand(tier: BrandTier, abs: number): BrandDef | null {
        const state = GameState.getInstance();
        const p = state.player;
        // 5 年拉黑窗口（2026-10-30 茄子令「放弃永久拉黑改成五年内」）：60 个月外的放弃自动解禁
        const blackout = GameConfig.ENDORSE_REJECT_BLACKOUT_MONTHS;
        const rejected = new Set((p.brandRejected ?? []).filter(e => abs - e.abs < blackout).map(e => e.id));
        // 结束合作冷却（2026-11-03 茄子令）：解约/自然到期后同品牌 N 月内不再递——⛔ 解约次月又递同品牌
        const cooled = (id: string): boolean => abs <= (p.brandCooldowns?.[id] ?? 0);
        const busyCategories = new Set((p.endorsements ?? []).filter(c => c.active).map(c => c.category));
        const pool = GameData.BRANDS.filter(b => b.tier === tier && !rejected.has(b.id) && !cooled(b.id) && !busyCategories.has(b.category));
        if (!pool.length) return null;
        return pool[Math.floor(Math.random() * pool.length)];
    }

        /**
     * 档→title（2026-11-03 扩展）：非高奢四档恒代言人；高奢 Lv4~6 挚友、Lv7 大使；
     * Lv8 起按咖位概率直接递代言人（概率小、咖位越高越大），未中仍递大使。
     */
    private titleForTier(tier: BrandTier): EndorseTitle {
        if (tier !== 'gs') return 'spokesman';
        const rank = this.rank();
        if (rank <= 6) return 'friend';
        const table = GameConfig.ENDORSE_GS_SPOKESMAN_PCT_BY_RANK;
        const pct = table[Math.min(rank, table.length) - 1] ?? 0;
        if (pct > 0 && Math.random() < pct) return 'spokesman';
        return 'ambassador';
    }

    /** 同档连抽去重：记住上次用的条目下标（内存态不入存档，纯防连月递约复读同一句） */
    private lastLineIdx: Record<string, number> = {};

    /** 随机挑一条点评，与上次不同条（两条制下=必换）。高奢按递约 title 分组取（挚友/大使/代言人语义不同） */
    private pickLine(tier: BrandTier, title: EndorseTitle): string {
        const lines = tier === 'gs' ? GS_TITLE_LINES[title] : BROKER_LINES[tier];
        const key = tier === 'gs' ? `gs:${title}` : tier;
        let i = Math.floor(Math.random() * lines.length);
        if (lines.length > 1 && this.lastLineIdx[key] === i) i = (i + 1) % lines.length;
        this.lastLineIdx[key] = i;
        return lines[i];
    }

    /** 模板插值：{品牌名}（台词已瘦身——报酬/年限/title 归签约弹窗显示，⛔ 回到台词里念 PPT） */
    private fillTemplate(tpl: string, brand: BrandDef): string {
        return tpl.replace('{品牌名}', brand.name);
    }

    // ===== 签约 / 放弃（两键强制二选一；UI 从 pendingBrandOffer 进入） =====

    /** 当前待签邀约的详情聚合（丙工位渲染重档弹窗用；无 pending 返回 null） */
    currentOffer(): { offer: PendingBrandOffer; brand: BrandDef } | null {
        const offer = GameState.getInstance().player.pendingBrandOffer;
        if (!offer) return null;
        const brand = GameData.BRAND_MAP[offer.brandId];
        return brand ? { offer, brand } : null;
    }

    /** 签约：排他再校验 → 钱（挚友活动完成即结/长约 5:5 头款）→ 微商掉口碑 → 长约落档 → 官宣名气+钩子 → 实绩 */
    accept(): EndorseResult {
        const state = GameState.getInstance();
        const p = state.player;
        const cur = this.currentOffer();
        if (!cur) return { ok: false, detail: '没有待签的商务邀约。' };
        const { offer, brand } = cur;
        // 续约邀约（同品牌在约中）豁免排他检查——自家品类自家续，⛔ 会被「同品类已占」拦死
        const isRenewalAccept = !!(p.endorsements ?? []).find(
            c => c.active && c.brandId === brand.id && c.title === offer.title);
        if (!isRenewalAccept && titleOccupies(brand.tier, offer.title) && (p.endorsements ?? []).some(c => c.active && c.category === brand.category)) {
            return { ok: false, detail: `${BRAND_CATEGORY_NAMES[brand.category]}已经有一份在约合作了，同品类签不了第二家。` };
        }
        p.pendingBrandOffer = null;

        if (offer.title === 'friend') {
            // 挚友=单次活动约：签约当月起，玩家在行程「工作」栏自行排一周完成（2026-11-02 茄子令），
            // 完成当周结费；当月未完成 → 月末违约
            const contract: EndorsementContract = {
                id: `ED_${brand.id}_${offer.month}`,
                brandId: brand.id,
                title: 'friend',
                tier: brand.tier,
                category: brand.category,
                signedAbs: offer.month,
                signedRank: this.rank(),
                monthsLeft: 0,
                annualFeeBase: this.baseFee(brand.tier, 'friend'),
                active: true,
                eventMonth: this.absMonth(),
                eventFee: offer.fee,
            };
            p.endorsements = [...(p.endorsements ?? []), contract];
            // 5:5 分期（2026-11-02 茄子令，同长约口径）：签约当场结头款 50%，完成时结清尾款 50%
            const advance = Math.floor(offer.fee / 2);
            state.addMoney(advance);
            p.endorseEarned = (p.endorseEarned ?? 0) + advance;
        } else {
            // 现实「5:5」分期（2026-10-30 茄子拍板，对齐代言合同首付/尾款惯例）：签约当场结 50% 头款，
            // 尾款 50% 约满结清（monthlySettle 的 monthsLeft 归零分支）——⛔ 已付头款不追（游戏从宽，
            // 违约金=全部惩罚）
            const head = Math.floor(offer.fee / 2);
            state.addMoney(head);
            p.endorseEarned = (p.endorseEarned ?? 0) + head;
            // 续约邀约=同品牌已有 active 长约 → 无缝转新约（重置期限/年费，⛔ 新建第二份）
            const existing = (p.endorsements ?? []).find(
                c => c.active && c.brandId === brand.id && c.title === offer.title);
            const annualBase = offer.fee / GameConfig.PAY_SCALE;
            if (existing) {
                existing.monthsLeft = offer.years * 12;
                existing.annualFeeBase = annualBase;   // 新一期按续约时点报价（名气涨=涨价正反馈）
                existing.signedAbs = offer.month;   // 头款已在上方公共段发放（5:5 照走，续约同式）
            } else {
                const contract: EndorsementContract = {
                    id: `ED_${brand.id}_${offer.month}`,
                    brandId: brand.id,
                    title: offer.title,
                    tier: brand.tier,
                    category: brand.category,
                    signedAbs: offer.month,
                    signedRank: this.rank(),
                    monthsLeft: offer.years * 12,   // 纯计时器（约满结尾款），⛔ 月付模型已废
                    // 5:5 口径一致性（2026-10-30 并行 AI 报数值 bug 后修）：存**报价的 ×1 还原值**（含品牌档
                    // 预算×名气系数）——尾款=annualFeeBase×SCALE/2=offer.fee/2，头尾合计恒等于报价；
                    // ⛔ 存纯基准（旧月付口径）会让尾款缺系数、总额比报价少 ~17%
                    annualFeeBase: annualBase,
                    active: true,
                };
                p.endorsements = [...(p.endorsements ?? []), contract];
            }
        }
        p.endorseTotal = (p.endorseTotal ?? 0) + 1;

        // 微商掉口碑（负向走总闸；黑料缠身 ×1.5 在 addAttribute 内自动生效）+随机网友群嘲句（填写表 §四）
        let mockText: string | undefined;
        if (brand.tier === 'ws') {
            const loss = GameConfig.ENDORSE_WS_REP_BASE + GameConfig.ENDORSE_WS_REP_PER_RANK * this.rank();
            state.addAttribute('reputation', -loss);
            const mocks = GameData.WS_MOCK_LINES;
            mockText = mocks.length ? mocks[Math.floor(Math.random() * mocks.length)] : undefined;
        }

        // 官宣（名气一次性；粉丝锚月末自动跟随）＋官宣微博入队（签约次月决策页弹 media 卡，2026-10-30 茄子令）
        // 续约⛔ 入队/⛔ 名气：续约不是新曝光事件（茄子令「续约不会再发官宣微博」），只有钱到账（复用上方 isRenewalAccept）
        if (!isRenewalAccept) {
            const fameGain = GameConfig.ENDORSE_ANNOUNCE_FAME[offer.title] as number;
            state.addFame(fameGain);
            p.pendingAnnounces = [...(p.pendingAnnounces ?? []), { brandId: brand.id, title: offer.title, absMonth: this.absMonth() }];
        }

        const titleName = ENDORSE_TITLE_NAMES[offer.title];
        const feeText = offer.title === 'friend'
            ? `头款${wan(Math.floor(offer.fee / 2))}已到账，尾款${wan(offer.fee - Math.floor(offer.fee / 2))}完成时结清`
            : `头款${wan(Math.floor(offer.fee / 2))}已到账，尾款${wan(offer.fee - Math.floor(offer.fee / 2))}约满结清`;
        return { ok: true, detail: `${brand.name}的${titleName}签好了。${feeText}。`, mockText };
    }

    /** 放弃：该品牌当周目不再递（不可逆，VN 台词已点明后果） */
    reject(): EndorseResult {
        const state = GameState.getInstance();
        const p = state.player;
        const offer = p.pendingBrandOffer;
        if (!offer) return { ok: false, detail: '没有待签的商务邀约。' };
        // 续约邀约放弃=和平分手（⛔ 不拉黑——不续约很正常，品牌未来仍可月掷再递；但盖 12 个月冷却）
        // 尾款照结（合作到期的既定报酬）+ 约即刻归档；新邀约放弃才进 5 年拉黑窗口
        const isRenewal = (p.endorsements ?? []).find(
            c => c.active && c.brandId === offer.brandId && c.title === offer.title);
        p.pendingBrandOffer = null;   // 两分支共用：表态完成即清通道
        if (isRenewal) {
            isRenewal.active = false;
            // 放弃续约=和平分手：按自然到期冷却，至少隔一年再递（2026-11-03 茄子令）
            this.stampBrandCooldown(offer.brandId, this.absMonth(), GameConfig.ENDORSE_EXPIRED_COOLDOWN_MONTHS);
            const tail = Math.floor(isRenewal.annualFeeBase * GameConfig.PAY_SCALE / 2);
            state.addMoney(tail);
            p.endorseEarned = (p.endorseEarned ?? 0) + tail;
            return { ok: true, detail: `回绝了，尾款${wan(tail)}照合同结给你，好聚好散。` };
        }
        {
            p.brandRejected = [
                ...(p.brandRejected ?? []).filter(e => e.id !== offer.brandId),
                { id: offer.brandId, abs: this.absMonth() },
            ];
        }
        p.pendingBrandOffer = null;
        return { ok: true, detail: '行，那我回绝了，这家以后不会再来了。' };
    }

    /**
     * 考察期晋升概率（A 版 2026-11-03 茄子令）：名气越大越容易升——
     * 基础 10% +（名气系数 − 1）× 40%，clamp 10%~50%。仅高奢大使约季末判定调用。
     */
    private promotePct(): number {
        const base = GameConfig.ENDORSE_PROMOTE_BASE_PCT
            + (this.fameMult() - 1) * GameConfig.ENDORSE_PROMOTE_FAME_BONUS;
        return Math.max(GameConfig.ENDORSE_PROMOTE_PCT_MIN, Math.min(GameConfig.ENDORSE_PROMOTE_PCT_MAX, base));
    }

    // ===== 月结（GameLoop doFinishMonth 内、advanceMonth 之前调用） =====

    /**
     * 月结算：解约判定（口碑崩/黑料=高奢·国民立即；掉咖位线连续 N 月，N=6）→ 约满结尾款 →
     * 季末高奢大使考察期晋升（40%）。顺序=先淘汰再结钱（被解约当月不发任何款项）。
     */
    monthlySettle(abs: number): void {
        const state = GameState.getInstance();
        const p = state.player;
        const contracts = p.endorsements ?? [];
        if (!contracts.length) return;

        const rep = state.getAttribute('reputation');
        const blackMarked = state.hasTag('TAG_401');

        for (const c of contracts) {
            if (!c.active) continue;
            const brand = GameData.BRAND_MAP[c.brandId];
            if (!brand) { c.active = false; continue; }

            // ⓪-旧档兼容（2026-11-02 前签约的挚友带 eventWeek 定档快照）：沿用旧规则在活动月月结
            // 自动完成结费，⛔ 让存量档被新违约规则误罚（新档无 eventWeek，不命中本分支）
            const legacyEventWeek = (c as EndorsementContract & { eventWeek?: number }).eventWeek;
            if (c.title === 'friend' && legacyEventWeek !== undefined && c.eventMonth !== undefined && abs >= c.eventMonth) {
                c.active = false;
                const fee = c.eventFee ?? 0;
                state.addMoney(fee);
                p.endorseEarned = (p.endorseEarned ?? 0) + fee;
                continue;
            }
            // ⓪ 挚友活动逾期：签约当月结束仍未在行程里完成 → 违约（2026-11-02 茄子令；
            // 完成与结费在玩家执行活动周时当场处理，⛔ 不再月结自动完成）
            if (c.title === 'friend' && c.eventMonth !== undefined && abs >= c.eventMonth) {
                this.breachFriend(c, brand, abs);
                continue;
            }

            // ① 品牌解约：黑料 / 口碑档（高奢、国民适用）——立即
            if (c.tier === 'gs' || c.tier === 'gm') {
                if (blackMarked) {
                    this.terminate(c, brand, abs, '黑料触发了合同条款');
                    continue;
                }
                if (rep <= GameConfig.REP_BAND_EDGES[1]) {
                    this.terminate(c, brand, abs, '口碑未达合同要求');
                    continue;
                }
            }
            // ② 品牌解约：名气连续 N 月低于品牌要求（2026-11-02 茄子令：判定口由咖位改名气，
            // 名气门槛取品牌要求咖位对应的 RANK_FAME_THRESHOLDS，⛔ 另存一张表）
            const minRank = GameConfig.ENDORSE_TIER_MIN_RANK[c.tier] ?? 1;
            const minFame = GameConfig.RANK_FAME_THRESHOLDS[minRank - 1] ?? 0;
            if (state.getFame() < minFame) {
                c.lowSinceAbs = c.lowSinceAbs ?? abs;
                if (abs - c.lowSinceAbs + 1 >= GameConfig.ENDORSE_LOWRANK_GRACE_MONTHS) {
                    this.terminate(c, brand, abs, '名气连续六个月低于品牌要求');
                    continue;
                }
            } else {
                c.lowSinceAbs = undefined;
            }

            // ③ 约满前最后一个月：先判续约（口径 A 拍板 2026-10-30 茄子令「a」）——
            // 达标（咖位≥线+口碑档≥T3+无黑料）→ 概率生成「续约邀约」走送达链（经纪人 VN+重档弹窗两键）；
            // 玩家签=旧约无缝转新约（accept 续约分支），放弃=和平分手（reject 特判：尾款照结+⛔ 不进 5 年拉黑）；
            // 不达标/未中概率=自然到期走下方尾款结算
            if (c.monthsLeft === 1 && this.renewalEligible(c, rep)
                && Math.random() < this.renewalPct(c.signedRank ?? this.rank())) {
                this.queueRenewalOffer(c, brand, abs);
                continue;   // 本月结算押后：玩家在决策页表态（签=重置期限，放弃=reject 内结清尾款）
            }
            c.monthsLeft -= 1;
            if (c.monthsLeft <= 0) {
                c.active = false;
                // 和平分手冷却（2026-11-03 茄子令）：到期台词说「后续再递约」，但至少隔一年才掷中
                this.stampBrandCooldown(c.brandId, abs, GameConfig.ENDORSE_EXPIRED_COOLDOWN_MONTHS);
                const tail = Math.floor(c.annualFeeBase * GameConfig.PAY_SCALE / 2);   // =签约报价的 50%
                state.addMoney(tail);
                p.endorseEarned = (p.endorseEarned ?? 0) + tail;
                this.noticeQueue.push({ kind: 'expired', brandId: c.brandId, text: `${brand.name}的合约到期了，尾款${wan(tail)}已结清。` });
            }

            // ④ 季末考察期晋升（高奢大使；咖位≥线+口碑未负+无黑料）
            if (
                c.active && c.title === 'ambassador' && c.tier === 'gs'
                && abs % 3 === 0
                && this.rank() >= (GameConfig.ENDORSE_TIER_MIN_RANK.gs ?? 7)
                && rep > GameConfig.REP_BAND_EDGES[1] && !blackMarked
                && Math.random() < this.promotePct()
            ) {
                c.title = 'spokesman';
                // 升约重签=按重签时点重新报价（预算×名气系数，×1 口径——与签约同式，⛔ 存纯基准）
                c.annualFeeBase = this.baseFee('gs', 'spokesman')
                    * (GameConfig.ENDORSE_TIER_BUDGET.gs ?? 1.2) * this.fameMult();
                c.monthsLeft = 12;   // 升约重签
                this.noticeQueue.push({ kind: 'promoted', brandId: c.brandId, text: `${brand.name}把你提到品牌代言人了，年费调整为${wan(Math.floor(c.annualFeeBase * GameConfig.PAY_SCALE))}，算是熬过考察期了。` });
                // 晋升=身份变化，发正式官宣（2026-10-30 茄子令；名气按代言人口径+20）
                state.addFame(GameConfig.ENDORSE_ANNOUNCE_FAME.spokesman as number);
                p.pendingAnnounces = [...(p.pendingAnnounces ?? []), { brandId: c.brandId, title: 'spokesman', absMonth: abs }];
            }
        }
    }

    /**
     * 结束合作盖章（2026-11-03 茄子令）：brandId 锁到 `fromAbs + months`（含锁当月），
     * 到期后 pickBrand 自动解禁。解约走 24 个月、自然到期/放弃续约走 12 个月。
     */
    private stampBrandCooldown(brandId: string, fromAbs: number, months: number): void {
        const p = GameState.getInstance().player;
        p.brandCooldowns = { ...(p.brandCooldowns ?? {}), [brandId]: fromAbs + months };
    }

    /** 品牌解约：违约金=年费×1（全额扣，存款不足可扣成负数=欠债；已付头款不追回）+待通报 */
    private terminate(c: EndorsementContract, brand: BrandDef, abs: number, reason: string): void {
        const state = GameState.getInstance();
        c.active = false;
        this.stampBrandCooldown(c.brandId, abs, GameConfig.ENDORSE_TERMINATE_COOLDOWN_MONTHS);
        const penalty = Math.floor(c.annualFeeBase * GameConfig.PAY_SCALE * GameConfig.ENDORSE_PENALTY_MULT);
        state.addMoney(-penalty);
        this.noticeQueue.push({
            kind: 'terminated',
            brandId: c.brandId,
            penalty,
            text: `${brand.name}已经终止合作，原因是${reason}。违约金${wan(penalty)}已从账上扣除。`,
        });
    }

    /** 玩家主动解约（经纪人处操作；同违约金，全额扣、存款不足可扣成负数） */
    terminateByPlayer(brandId: string): EndorseResult {
        const state = GameState.getInstance();
        const p = state.player;
        const c = (p.endorsements ?? []).find(x => x.active && x.brandId === brandId);
        if (!c) return { ok: false, detail: '这家没有在约的合作。' };
        const brand = GameData.BRAND_MAP[brandId];
        c.active = false;
        this.stampBrandCooldown(brandId, this.absMonth(), GameConfig.ENDORSE_TERMINATE_COOLDOWN_MONTHS);
        const penalty = Math.floor(c.annualFeeBase * GameConfig.PAY_SCALE * GameConfig.ENDORSE_PENALTY_MULT);
        state.addMoney(-penalty);
        return {
            ok: true,
            detail: `${brand?.name ?? brandId}的合作已经解除，违约金${wan(penalty)}已按合同扣除。后续如有同类邀约，我会先和你确认。`,
        };
    }

    // ===== 续约（口径 A：商务表现判定，2026-10-30 茄子拍板「a」）=====

    /**
     * 续约资格（2026-10-30 茄子令改口径「口碑档达到品牌要求线就行」）：咖位仍 ≥ 品牌要求线 +
     * 口碑档达到**本档递约资格线**（同尺递约资格：微商无要求、潮流/新消费 ≥T2（>-301）、
     * 国民/高奢 ≥T3（>-101））。黑料不单列——高奢/国民挂黑料在解约条款已先行处理，
     * 微商/潮流/新消费本就黑红友好续约。
     */
    private renewalEligible(c: EndorsementContract, rep: number): boolean {
        if (this.rank() < (GameConfig.ENDORSE_TIER_MIN_RANK[c.tier] ?? 1)) return false;
        if (c.tier === 'ws') return true;                          // 微商：T1 都续（不在乎口碑）
        if (c.tier === 'cl' || c.tier === 'xf') return rep > GameConfig.REP_BAND_EDGES[0];   // ≥T2
        return rep > GameConfig.REP_BAND_EDGES[1];                 // 国民/高奢：≥T3
    }

    /**
     * 续约概率（2026-10-30 茄子令终版公式）：40% 基础 + 同咖名气溢出加成（fameMult>1 的正值部分 ×50%
     * ——同咖位越来越红=品牌方抢着签红人）− 升咖跳档惩罚（签约后每升 1 级咖 −15%——重报价跳一档，
     * 现实=层级跃迁后原品牌续约谈判变难），clamp 10%~80%。
     */
    private renewalPct(signedRank: number): number {
        const rankUp = Math.max(0, this.rank() - signedRank);
        const overflow = Math.max(0, this.fameMult() - 1) * 0.5;
        return Math.max(0.1, Math.min(0.8, 0.4 + overflow - rankUp * 0.15));
    }

    /**
     * 生成续约邀约（复用 pendingBrandOffer 通道，⛔ 新组件）：同一品牌的新 offer，
     * brokerLine 换续约话术、fee=按续约时点重新报价（基准×预算×名气系数×SCALE）、年限 1 年。
     * pendingBrandOffer 若被占（罕见：续约月与其他递约撞）→ 放弃续约=自然到期，不丢月结。
     */
    private queueRenewalOffer(c: EndorsementContract, brand: BrandDef, abs: number): void {
        const state = GameState.getInstance();
        if (state.player.pendingBrandOffer) return;   // 通道被占：本轮不续（下月 monthsLeft=0 走自然到期）
        const title = c.title === 'friend' ? 'spokesman' : c.title;   // 挚友无续约（不会进本分支），防御兜底
        const budget = GameConfig.ENDORSE_TIER_BUDGET[c.tier] ?? 1;
        const fee = Math.floor(this.baseFee(c.tier, title) * budget * this.fameMult() * GameConfig.PAY_SCALE);
        const lines: Record<BrandTier, string> = {
            ws: `{品牌名}想跟你续约——他们挺满意这段时间的数据，条件不变，还是那份钱。续不续你定。`,
            cl: `{品牌名}要续约。合作愉快，风格也对，接着穿就是了。`,
            xf: `{品牌名}提出续约。带货数据他们很认可，新的一期条件照旧谈。`,
            gm: `{品牌名}提出续约。国民牌子认人，续上就是老朋友，你考虑清楚。`,
            gs: `{品牌名}对你的表现很满意，提出续约——考察期已经通过，新合同直接接上。`,
        };
        state.player.pendingBrandOffer = {
            brandId: c.brandId,
            title,
            fee,
            years: 1,
            month: abs,
            brokerLine: (lines[c.tier] ?? '').replace('{品牌名}', brand.name),
        };
    }

    /** 在约长约（助理侧列表/排他判定用） */
    activeContracts(): EndorsementContract[] {
        return (GameState.getInstance().player.endorsements ?? []).filter(c => c.active);
    }

    /**
     * 已定档活动占用的自由日周（综艺录制与 NPC 邀约/综艺飞行同占自由日、周级互斥）。
     * 2026-11-02 起品牌挚友改为正常工作行程，⛔ 不再占自由日（本方法只统计综艺录制）。
     * **带原因版**（occupiedEventDetail）给周选 UI 显示占用原因，本方法只回周号集合。
     */
    occupiedEventWeeks(absMonth: number): number[] {
        return this.occupiedEventDetail(absMonth).map(d => d.week);
    }

    /** 占用明细（周号+原因）：综艺录制=「综艺录制」（CharactersPanel 周选行显示） */
    occupiedEventDetail(absMonth: number): { week: number; reason: string }[] {
        const p = GameState.getInstance().player;
        const out: { week: number; reason: string }[] = [];
        for (const v of p.varietyGuests ?? []) {
            if (v.active && v.eventMonth === absMonth) out.push({ week: v.eventWeek, reason: '综艺录制' });
        }
        return out;
    }

    /**
     * 官宣文案渲染（丁导码表 ANNOUNCE_COPY；官宣弹窗调用）：插值 {品牌名}{title}{玩家名}——
     * title 传实际签约 title（挚友走 friend 条、大使/代言人共用 formal 条），@ 前缀已在模板内。
     * 无文案的品牌返回 null（填写表漏行时的兜底，⛔ 渲染端再造句子）。
     */
    renderAnnounce(brandId: string, title: EndorseTitle, playerName: string): string | null {
        const copy = GameData.ANNOUNCE_COPY[brandId];
        if (!copy) return null;
        const brand = GameData.BRAND_MAP[brandId];
        const tpl = title === 'friend' ? (copy.friend ?? copy.formal) : copy.formal;
        return tpl
            .replace(/\{品牌名\}/g, brand?.name ?? '')
            .replace(/\{title\}/g, ENDORSE_TITLE_NAMES[title])
            .replace(/\{玩家名\}/g, playerName);
    }

    /**
     * 组装官宣 media 微博卡（2026-10-30 茄子令）：博主名=品牌名、正文=§五 文案（带 #话题# 已高亮）、
     * 热评=§5.1 对应档位 3 条。buzz（互动读数锚）留给呈现层按当时名气填，本层不掺数值旋钮。
     * 数据缺失（品牌/文案任缺）返回 null，调用方跳过这一条。
     */
    announceMedia(a: PendingBrandAnnounce): Omit<FixedMedia, 'buzz'> | null {
        const brand = GameData.BRAND_MAP[a.brandId];
        if (!brand) return null;
        const p = GameState.getInstance().player;
        const body = this.renderAnnounce(a.brandId, a.title, p.name);
        if (!body) return null;
        const comments = GameData.ANNOUNCE_COMMENTS;
        return {
            kind: 'weibo',
            author: brand.name,
            body,
            comments: comments.map(c => c.replace(/\{玩家名\}/g, p.name)),
        };
    }

    /**
     * 消费一条待弹官宣（确认行程链 `app.playAnnounceThen` 轮询；空返回 null）。
     * 时机由挂点保证：签约当刻只入队，玩家点「确认行程」、小助理播报/颁奖夜收完后才轮到它
     * （月度执行之前，与特殊剧情 media 同一只 MediaPanel）。日期判据只做脏数据护栏（absMonth ≤ 当前月）。
     */
    consumeAnnounce(): PendingBrandAnnounce | null {
        const p = GameState.getInstance().player;
        const queue = p.pendingAnnounces ?? [];
        const now = this.absMonth();
        const idx = queue.findIndex(a => a.absMonth <= now);
        if (idx < 0) return null;
        const [a] = queue.splice(idx, 1);
        p.pendingAnnounces = queue;
        return a;
    }

    /** 消费一条待通报（决策页入场时轮询；空返回 null） */
    consumeNotice(): EndorseNotice | null {
        return this.noticeQueue.shift() ?? null;
    }

    // ===== 挚友活动：完成（工作周执行）与逾期违约（月结） =====

    /**
     * 完成品牌活动（2026-11-02 茄子令）：玩家在行程「工作」栏排到该周、执行时调用；
     * 结清尾款 50%（头款签约已付），⛔ 不发完成通报。
     */
    completeFriendEvent(endorsementId: string): boolean {
        const state = GameState.getInstance();
        const c = (state.player.endorsements ?? []).find(x => x.id === endorsementId && x.active && x.title === 'friend');
        if (!c) return false;
        const fee = c.eventFee ?? 0;
        const tail = fee - Math.floor(fee / 2);
        c.active = false;
        state.addMoney(tail);
        state.player.endorseEarned = (state.player.endorseEarned ?? 0) + tail;
        return true;
    }

    /** 品牌活动逾期违约：违约金=活动费×1（全额，存款不足可扣成负数）+ 经纪人通报（2026-11-02 茄子令） */
    private breachFriend(c: EndorsementContract, brand: BrandDef, abs: number): void {
        const state = GameState.getInstance();
        c.active = false;
        // 违约=关系破裂，按解约口径冷却（2026-11-03 茄子令）
        this.stampBrandCooldown(c.brandId, abs, GameConfig.ENDORSE_TERMINATE_COOLDOWN_MONTHS);
        const feeBase = c.eventFee ?? Math.floor(c.annualFeeBase * GameConfig.PAY_SCALE);
        const penalty = Math.floor(feeBase * GameConfig.ENDORSE_PENALTY_MULT);
        state.addMoney(-penalty);
        this.noticeQueue.push({
            kind: 'breached',
            brandId: c.brandId,
            penalty,
            text: `${brand.name}的品牌活动没有在期限内完成，按合同扣除违约金${wan(penalty)}。`,
        });
    }
}

/** title 是否占用品类排他位（挚友无排他——单次合作不锁品类） */
function titleOccupies(tier: BrandTier, title: EndorseTitle): boolean {
    return title !== 'friend';
}

/** 金额→「x万」展示（万元取整到一位小数；<1万 显示元） */
function wan(n: number): string {
    if (n < 10000) return `${n}元`;
    const w = n / 10000;
    const text = Number.isInteger(w) ? String(w) : w.toFixed(1);
    return `${text}万`;
}

/** 导出单例 */
export const endorsementSystem = EndorsementSystem.getInstance();
